import axios from 'axios';
import sortBy from 'lodash/sortBy';
import { useEffect, useState } from 'react';

import { Select } from './Select';

interface SelectOption {
    value: string;
    label: string;
}

interface Props {
    name: string;
    ajaxUrl: string;
    ajaxHeaders?: Record<string, string>;
    ajaxParams?: Record<string, unknown>;
    className?: string;
    labelFn?: (option: Record<string, unknown>) => string;
    valueFn?: (option: Record<string, unknown>) => string | number;
    placeholder?: string;
    isClearable?: boolean;
    isDisabled?: boolean;
}

/**
 * RHF analog of `Ui/Form/AjaxSelect.jsx`. Fetches the full option list
 * once on mount and renders an RHF `<Select>`. Unlike `<AjaxSearch>`,
 * the dropdown is client-filtered and shows everything up front — use
 * this for small bounded option sets (≤ a few hundred items).
 */
export function AjaxSelect({
    name,
    ajaxUrl,
    ajaxHeaders,
    ajaxParams,
    className,
    labelFn = (option) => {
        return option.name as string;
    },
    valueFn = (option) => {
        return option.id as string | number;
    },
    placeholder,
    isClearable,
    isDisabled,
}: Props) {
    const [
        options,
        setOptions,
    ] = useState<SelectOption[] | null>(null);

    useEffect(() => {
        if (!ajaxUrl) {
            return;
        }

        axios.get(ajaxUrl, { params: ajaxParams, headers: ajaxHeaders }).then(({ data }) => {
            setOptions(
                sortBy(
                    data.results.map((option: Record<string, unknown>) => {
                        return {
                            label: labelFn(option),
                            // Select expects string values; coerce numeric ids at the boundary.
                            value: String(valueFn(option)),
                        };
                    }),
                    'label'
                )
            );
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Select
            className={ className }
            isClearable={ isClearable }
            isDisabled={ isDisabled || options === null }
            name={ name }
            options={ (options ?? [
                { label: 'Loading…', value: '' },
            ]) as SelectOption[] }
            placeholder={ placeholder }
        />
    );
}
